import { useEffect, useState, type FormEvent } from 'react';
import { ApiError, useSession } from '@huaca/ui';
import { createInstance, listTemplates } from '../api';
import type { Instance, Template } from '../types';

/** "Start New Onboarding": client details + a template → creates the instance and its share link. */
export function StartOnboardingModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (instance: Instance) => void;
}) {
  const { client } = useSession();
  const [templates, setTemplates] = useState<Template[] | null>(null);
  const [templateId, setTemplateId] = useState('');
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    listTemplates(client)
      .then((t) => {
        if (!live) return;
        setTemplates(t);
        setTemplateId((prev) => prev || t[0]?._id || '');
      })
      .catch(() => live && setError('לא ניתן לטעון תבניות.'));
    return () => {
      live = false;
    };
  }, [client]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const instance = await createInstance(client, { templateId, client: { fullName, phone, email } });
      onCreated(instance);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'לא ניתן ליצור את המפרט. נסו שוב.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="claro-modal-backdrop" role="dialog" aria-modal="true" aria-label="תהליך חדש">
      <div className="claro-modal" data-testid="start-onboarding-modal">
        <h2>תהליך חדש</h2>
        <form className="huaca-form" onSubmit={submit}>
          <label htmlFor="claro-client-name">שם מלא של הלקוח</label>
          <input id="claro-client-name" required value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="דנה לוי" />

          <label htmlFor="claro-client-phone">טלפון</label>
          <input id="claro-client-phone" required dir="ltr" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="050-1234567" />

          <label htmlFor="claro-client-email">אימייל</label>
          <input id="claro-client-email" type="email" required dir="ltr" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="dana@example.com" />
          <p className="claro-hint">הלקוח יאמת את הכתובת הזו בכניסה לחדר.</p>

          <label htmlFor="claro-template">תבנית</label>
          <select id="claro-template" required value={templateId} onChange={(e) => setTemplateId(e.target.value)} data-testid="template-select">
            {!templates && <option value="">טוען…</option>}
            {templates?.length === 0 && <option value="">אין תבניות פעילות</option>}
            {templates?.map((t) => (
              <option key={t._id} value={t._id}>
                {t.name}
              </option>
            ))}
          </select>

          {error && <p role="alert">{error}</p>}

          <div className="claro-modal-actions">
            <button type="button" onClick={onClose}>
              ביטול
            </button>
            <button type="submit" className="btn-primary" disabled={busy || !templateId} data-testid="create-instance">
              {busy ? 'יוצר…' : 'יצירת קישור'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
